import {
  ComponentRef,
  DestroyRef,
  Directive,
  Type,
  ViewContainerRef,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { PlanningItemDto } from '@digiwall/goliath-documents/proxy';
import { FilterSettingsMap, PlanningBoardFilterState } from '../planning-common/filters';

/** The board filter contract every per-source filter component satisfies. */
interface BoardFilterInstance {
  filterChange?: { subscribe: (next: (predicate: (dto: PlanningItemDto) => boolean) => void) => unknown };
  stateChange?: { subscribe: (next: (state: PlanningBoardFilterState) => void) => unknown };
}

/**
 * Hosts a data source's board filter component in the toolbar. NgComponentOutlet cannot bind outputs,
 * so this directive creates the component imperatively, keeps its `filterSettings`/`items` inputs in
 * sync, and re-emits its `filterChange` predicate. One directive instance per resolved source.
 */
@Directive({
  selector: '[documentPlanningFilterHost]',
})
export class PlanningFilterHostDirective {
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly documentPlanningFilterHost = input.required<Type<unknown>>();
  readonly filterSettings = input<FilterSettingsMap>({});
  readonly items = input<PlanningItemDto[]>([]);
  /** Restored filter selection to seed the hosted component with (from persisted view state). */
  readonly state = input<PlanningBoardFilterState>();
  readonly filterChange = output<(dto: PlanningItemDto) => boolean>();
  readonly stateChange = output<PlanningBoardFilterState>();

  private ref?: ComponentRef<unknown>;

  constructor() {
    effect(() => {
      const component = this.documentPlanningFilterHost();
      if (!this.ref || this.ref.componentType !== component) {
        this.ref?.destroy();
        this.ref = this.viewContainerRef.createComponent(component);
        const instance = this.ref.instance as BoardFilterInstance;
        instance.filterChange?.subscribe(predicate => this.filterChange.emit(predicate));
        instance.stateChange?.subscribe(state => this.stateChange.emit(state));
      }
      // Keep inputs in sync on every change.
      this.ref.setInput('filterSettings', this.filterSettings());
      this.ref.setInput('items', this.items());
      this.ref.setInput('state', this.state());
    });

    this.destroyRef.onDestroy(() => this.ref?.destroy());
  }
}
